import { XYChart, BarSeries, Axis, buildChartTheme } from "@visx/xychart";
import { ClimbType } from "griptonite-api-client";
import max from "lodash/max";
import min from "lodash/min";
import { useMemo } from "react";

import { Route } from "../../../core/types";
import { useGradesQuery } from "../../../hooks/grades";
import { useCurrentClimbType, useCurrentVenueQuery } from "../../../hooks/venues";
import { convertFromInternal, getAllGradeValuesForSystem } from "../../../utils/grades";
import { useRoutesUiContext } from "../../RoutesProvider/RoutesProvider";

const chartTheme = buildChartTheme({
  backgroundColor: "white",
  colors: [],
  gridColor: "#D4D6DE",
  gridColorDark: "#D4D6DE",
  svgLabelSmall: {
    fill: "#6E7891",
  },
  tickLength: 2,
  xTickLineStyles: {
    stroke: "#D4D6DE",
    strokeWidth: 1,
  },
  xAxisLineStyles: {
    stroke: "#D4D6DE",
    strokeWidth: 1,
  },
  yTickLineStyles: {
    stroke: "#D4D6DE",
    strokeWidth: 1,
  },
  yAxisLineStyles: {
    stroke: "#D4D6DE",
    strokeWidth: 1,
  },
});

const getCountsByGrade = (routes: Route[], gradeSystem: string, allRoutes?: Route[]) => {
  allRoutes = allRoutes || routes;
  const counts = new Map(getAllGradeValuesForSystem(gradeSystem).map((grade) => [grade, 0]));
  counts.set("?", 0);
  for (const route of routes) {
    const grade = convertFromInternal(route.gradeInternal, gradeSystem) || "?";
    if (grade) {
      counts.set(grade, (counts.get(grade) || 0) + 1);
    }
  }
  const minGradeInternal =
    min(allRoutes?.filter((route) => route.gradeInternal).map((route) => route.gradeInternal)) ??
    null;
  const minGrade = convertFromInternal(minGradeInternal, gradeSystem);
  const maxGradeInternal =
    max(allRoutes?.filter((route) => route.gradeInternal).map((route) => route.gradeInternal)) ??
    null;
  const maxGrade = convertFromInternal(maxGradeInternal, gradeSystem);

  let result = Array.from(counts.entries(), ([grade, count]) => ({
    grade,
    count,
  }));

  // Filter the result to only counts that are within the range of the min and max grades
  const minGradeIndex = result.findIndex(({ grade }) => grade === minGrade);
  const maxGradeIndex = result.findIndex(({ grade }) => grade === maxGrade);
  result = result.slice(minGradeIndex, maxGradeIndex + 1);

  // Add unknown grade count to the end if it's nonzero
  const nullCount = counts.get("?");
  if (!result.find(({ grade }) => grade === "?") && nullCount) {
    result.push({ grade: "?", count: nullCount });
  }
  return result;
};

export default function GradeHistogram() {
  const { routes, filteredRoutes } = useRoutesUiContext();
  const { data: venue } = useCurrentVenueQuery();
  const climbType = useCurrentClimbType();
  const gradeSystem =
    climbType === "SPORT"
      ? venue?.sportGradeSystem || "FRENCH"
      : venue?.boulderGradeSystem || "FONT";

  const { isFetched: isGradesLoaded } = useGradesQuery();

  const countsByGrade = useMemo(() => {
    if (!isGradesLoaded) {
      return [];
    }
    return getCountsByGrade(filteredRoutes, gradeSystem, routes);
  }, [filteredRoutes, gradeSystem, routes, isGradesLoaded]);

  const allCountsByGrade = useMemo(() => {
    if (!isGradesLoaded) {
      return [];
    }
    return getCountsByGrade(routes || [], gradeSystem);
  }, [routes, gradeSystem, isGradesLoaded]);

  console.log("all counts", allCountsByGrade);

  if (!climbType) {
    return;
  }

  return (
    <GradeHistogramChart
      allCountsByGrade={allCountsByGrade}
      countsByGrade={countsByGrade}
      climbType={climbType}
    />
  );
}

export const GradeHistogramChart = ({
  allCountsByGrade,
  countsByGrade,
  climbType,
}: {
  allCountsByGrade: { grade: string; count: number }[];
  countsByGrade: { grade: string; count: number }[];
  climbType: ClimbType;
}) => {
  return (
    <XYChart
      height={100}
      xScale={{ type: "band", padding: 0.2 }}
      yScale={{ type: "linear" }}
      margin={{ top: 4, left: 24, right: 16, bottom: 20 }}
      theme={chartTheme}
    >
      <BarSeries
        data={allCountsByGrade}
        dataKey="All Grades"
        xAccessor={(d) => d.grade}
        yAccessor={(d) => d.count}
        barPadding={0.4}
        colorAccessor={() => "#F1F4F7"}
        radiusTop
        radius={2}
      />
      <BarSeries
        data={countsByGrade}
        dataKey="Grades"
        xAccessor={(d) => d.grade}
        yAccessor={(d) => d.count}
        barPadding={0.4}
        colorAccessor={() => (climbType === "SPORT" ? "#BB6BD9" : "#4579FF")}
        radiusTop
        radius={2}
      />
      <Axis orientation="bottom" />
      <Axis orientation="left" numTicks={3} />
    </XYChart>
  );
};
